import React, { useEffect, useState, useCallback } from "react";
import { useSidebar } from "../context/SidebarContext";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { clearAppCache } from "../utils";
import {
  Home,
  MapPin,
  LineChart,
  Megaphone,
  ChevronDown,
  Menu,
  X,
  Building2,
} from "lucide-react";

declare const API_BASE_URL: string;

type SubItem = {
  name: string;
  path: string;
};

type NavItem = {
  key: string;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  path?: string;
  subItems?: SubItem[];
};

const NAV_ITEMS: NavItem[] = [
  { key: "dashboard", name: "Dashboard", icon: Home, path: "/" },
  {
    key: "outlet",
    name: "Outlet",
    icon: MapPin,
    subItems: [
      { name: "Detail Outlet", path: "/outlet/detail" },
      { name: "Employee", path: "/outlet/employee" },
      { name: "Pest Control", path: "/outlet/pestcontrol" },
    ],
  },
  {
    key: "tenant-relation",
    name: "Tenant Relation",
    icon: Megaphone,
    subItems: [
      { name: "Announcements", path: "/tenant-relation/announcements" },
      { name: "Safety Task", path: "/safetyTask" },
      // { name: "Work Permit", path: "/workPermit" },
      { name: "Tenant Request", path: "/tenantRequest" },
      { name: "Mall Traffic", path: "/tenant-relation/visitor" },
      { name: "PlayUp", path: "/tenant-relation/playup" },
      { name: "LinkNet", path: "/tenant-relation/linknet" },
      // { name: "HyperNet", path: "/tenant-relation/hypernet" },
    ],
  },
  {
    key: "sales",
    name: "Sales",
    icon: LineChart,
    subItems: [
      { name: "Sales Upload", path: "/sales/upload" },
      { name: "Summary", path: "/sales/summary" },
      { name: "Transaction", path: "/sales/transaction" },
      { name: "Revenue Monitor", path: "/sales/revenue-monitor" },
    ],
  },
  // {
  //   key: "vendor-management",
  //   name: "Vendor Management",
  //   icon: Building2,
  //   subItems: [
  //     { name: "Detail Vendor", path: "/vendor-management/detail" },
  //     { name: "Employee", path: "/vendor-management/employee" },
  //     { name: "Work Permit", path: "/vendor-management/work-permit" },
  //   ],
  // },
];

type UserData = {
  displayName?: string;
  email?: string;
  avatar?: string | null;
};

export default function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isExpanded,
    setHovered,
    toggleSidebar,
    isMobileOpen,
    toggleMobileSidebar,
  } = useSidebar();

  const [openSubmenu, setOpenSubmenu] = useState<string | null>("dashboard");
  const [activeItem, setActiveItem] = useState("dashboard");
  const [activeSubItem, setActiveSubItem] = useState<string | null>(null);
  const collapsed = !isExpanded;

  const [, setUser] = useState<UserData>({
    displayName: "User",
    email: "—",
  });
  const [, setLoading] = useState(false);
  const [, setError] = useState<string | null>(null);

  const signOut = useCallback((redirect = true) => {
    clearAppCache();
    if (redirect) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const handleMainItemClick = (item: NavItem) => {
    setActiveItem(item.key);
    if (item.path) {
      navigate(item.path);
      setActiveSubItem(null);
    }
    if (item.subItems?.length) {
      setOpenSubmenu((prev) => (prev === item.key ? null : item.key));
    } else {
      setOpenSubmenu(null);
    }
  };

  const handleSubItemClick = (
    item: NavItem,
    sub: SubItem,
    subIndex: number,
  ) => {
    const key = `${item.key}-${subIndex}`;
    setActiveItem(item.key);
    setActiveSubItem(key);
    navigate(sub.path);
    if (isMobileOpen) {
      toggleMobileSidebar();
    }
    setOpenSubmenu(item.key);
  };

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileOpen]);

  useEffect(() => {
    const storedEmail =
      localStorage.getItem("user_email") || sessionStorage.getItem("user_email");
    const storedPassword =
      localStorage.getItem("user_password") || sessionStorage.getItem("user_password");
    const token =
      localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
    const storedMallId =
      localStorage.getItem("mall_id") || sessionStorage.getItem("mall_id");

    if (!storedEmail) {
      return;
    }

    if (!token) {
      signOut();
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_BASE_URL}/user/v1/getuserrole`;
        const resp = await axios.get(url, {
          headers: {
            "X-Username": storedEmail,
            "X-Password": storedPassword || "",
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        const roleData = resp.data;
        let displayName = "";

        if (roleData?.userRoleDatas && roleData.userRoleDatas.length > 0) {
          const matchRole = roleData.userRoleDatas.find(
            (r: { MallId?: number | string; MallName: string }) => r.MallId?.toString() === storedMallId
          );
          displayName = matchRole ? matchRole.MallName : roleData.userRoleDatas[0].MallName;
        }

        if (!displayName) {
          displayName = storedEmail.split("@")[0];
        }

        if (!cancelled) {
          setUser({ displayName, email: storedEmail });
        }
      } catch (err) {
        const errorMsg = err as { response?: { status?: number } };
        console.error("Fetch user failed", err);
        if (!cancelled) {
          if (errorMsg?.response?.status === 401) {
            signOut();
            return;
          }
          setError("Gagal mengambil data user");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUser();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [signOut]);

  useEffect(() => {
    const path = (location.pathname || "/").toLowerCase();
    let matchedItemKey: string | null = null;
    let matchedSubKey: string | null = null;

    for (const item of NAV_ITEMS) {
      if (!item.subItems) continue;
      for (let i = 0; i < item.subItems.length; i++) {
        const sub = item.subItems[i];
        if (!sub.path) continue;
        const subPath = sub.path.toLowerCase();
        if (
          subPath === path ||
          path.startsWith(subPath + "/") ||
          path.startsWith(subPath)
        ) {
          matchedItemKey = item.key;
          matchedSubKey = `${item.key}-${i}`;
          break;
        }
      }
      if (matchedItemKey) break;
    }

    if (!matchedItemKey) {
      for (const item of NAV_ITEMS) {
        if (item.path) {
          const itemPath = item.path.toLowerCase();
          if (
            itemPath === path ||
            path.startsWith(itemPath + "/") ||
            (path === "/" && itemPath === "/")
          ) {
            matchedItemKey = item.key;
            matchedSubKey = null;
            break;
          }
        }
      }
    }

    if (!matchedItemKey) {
      matchedItemKey = "dashboard";
      matchedSubKey = null;
    }

    setActiveItem(matchedItemKey);
    setActiveSubItem(matchedSubKey);

    if (matchedSubKey) {
      setOpenSubmenu(matchedItemKey);
    } else {
      setOpenSubmenu((prev) =>
        NAV_ITEMS.find((i) => i.key === matchedItemKey && i.subItems?.length)
          ? matchedItemKey
          : prev,
      );
    }
  }, [location.pathname]);

  return (
    <>
      <div
        className={`hidden lg:flex flex-col h-screen fixed left-0 top-0 bottom-0 z-50 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transition-all duration-300 ${collapsed ? "w-16" : "w-[208px]"
          }`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="flex items-center justify-between px-3.5 py-4 border-b border-gray-200 dark:border-gray-800">
          {!collapsed && (
            <h1 className="text-base font-extrabold text-gray-800 dark:text-white whitespace-nowrap tracking-tight">
              Tenant Access
            </h1>
          )}
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const hasChildren = !!item.subItems?.length;
            const isOpen = openSubmenu === item.key;
            const isActive = activeItem === item.key;

            return (
              <div key={item.key}>
                <button
                  onClick={() => handleMainItemClick(item)}
                  className={`w-full flex items-center rounded-xl px-2.5 py-2.5 text-sm font-medium transition-all duration-200 ${isActive
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
                    } ${collapsed ? "justify-center" : ""}`}
                  title={collapsed ? item.name : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <span className={`ml-2.5 flex-1 text-left whitespace-nowrap ${item.key === "vendor-management" ? "text-xs" : ""}`}>{item.name}</span>
                      {hasChildren && (
                        <ChevronDown
                          className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                            }`}
                        />
                      )}
                    </>
                  )}
                </button>

                {hasChildren && !collapsed && (
                  <div
                    className={`overflow-hidden transition-all duration-200 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                      }`}
                  >
                    <div className="pl-11 pr-2 pt-1 space-y-1">
                      {item.subItems!.map((sub, i) => {
                        const key = `${item.key}-${i}`;
                        const active = activeSubItem === key;
                        return (
                          <button
                            key={key}
                            onClick={() => handleSubItemClick(item, sub, i)}
                            className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-all duration-200 ${active
                              ? "text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20"
                              : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                              }`}
                          >
                            {sub.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {isMobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={toggleMobileSidebar}
          />
          <div className="relative w-56 bg-white dark:bg-gray-900 h-full flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
              <h1 className="text-xl font-bold text-gray-800 dark:text-white">
                Tenant Access
              </h1>
              <button
                onClick={toggleMobileSidebar}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const hasChildren = !!item.subItems?.length;
                const isOpen = openSubmenu === item.key;
                const isActive = activeItem === item.key;

                return (
                  <div key={item.key}>
                    <button
                      onClick={() => {
                        setActiveItem(item.key);
                        if (item.path) {
                          navigate(item.path);
                          toggleMobileSidebar();
                          setActiveSubItem(null);
                        }
                        if (hasChildren) {
                          setOpenSubmenu((p) =>
                            p === item.key ? null : item.key,
                          );
                        }
                      }}
                      className={`w-full flex items-center rounded-xl px-2.5 py-2.5 text-sm font-medium transition-colors ${isActive
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                        }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className={`ml-2.5 flex-1 text-left whitespace-nowrap ${item.key === "vendor-management" ? "text-xs" : ""}`}>{item.name}</span>
                      {hasChildren && (
                        <ChevronDown
                          className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""
                            }`}
                        />
                      )}
                    </button>

                    {hasChildren && (
                      <div
                        className={`overflow-hidden transition-all ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                          }`}
                      >
                        <div className="pl-11 pr-2 pt-1 space-y-1">
                          {item.subItems!.map((sub, i) => {
                            const key = `${item.key}-${i}`;
                            const active = activeSubItem === key;
                            return (
                              <button
                                key={key}
                                onClick={() => handleSubItemClick(item, sub, i)}
                                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${active
                                  ? "text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20"
                                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                                  }`}
                              >
                                {sub.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}